import React from 'react';
import TopNav from './TopNav';
import Footer from './Footer';
import useSEO from '../hooks/useSEO';

function AntiFragileNexusPage({ logo }) {
  useSEO({
    title: 'Anti-Fragile Nexus | Fall 2026 Experiential Learning',
    description:
      'A live multiplayer systems workshop for Fall 2026. Help participants understand non-linear resilience, cross-functional tradeoffs, and fragility lag in volatile supply chains.'
  });

  const outcomes = [
    'Identify reinforcing and balancing loops that accelerate recovery or collapse.',
    'Recognize fragility debt before short-term efficiency gains become systemic risk.',
    'Evaluate tradeoffs across People, Planet, and Profit using stock-and-flow reasoning.',
    'Explain why delayed consequences create false confidence in early performance metrics.'
  ];

  const instructorBenefits = [
    {
      title: 'Behavioral Evidence, Not Just Theory',
      body:
        'Participants experience policy consequences live, creating clear classroom evidence for systems concepts that are often abstract in lecture-only settings.'
    },
    {
      title: 'Accessible to Mixed Experience Levels',
      body:
        'Designed for beginners while still producing rich analysis for advanced students, practitioners, and experienced systems thinkers.'
    },
    {
      title: 'Fits In-Person, Remote, or Hybrid Delivery',
      body:
        'The cloud-based format supports mixed attendance and makes it practical for programs with distributed cohorts.'
    },
    {
      title: 'Directly Relevant to Modern Programs',
      body:
        'Connects system dynamics with resilience, risk, policy, sustainability, and operations leadership without oversimplifying tradeoffs.'
    }
  ];

  const phases = [
    {
      phase: 'Phase 1: The Steady State',
      text:
        'Teams orient to the stock-and-flow structure and see how People and Planet conditions shape productivity, regulatory friction, and long-run capacity.'
    },
    {
      phase: 'Phase 2: The Stress Test',
      text:
        'Teams compete on ROI under stochastic disruptions, negotiate cross-functional tradeoffs in real time, and manage competing KPIs under pressure.'
    },
    {
      phase: 'Phase 3: The Resilience Autopsy',
      text:
        'Causal trace analysis reveals why some teams recovered faster and why non-financial buffers often determine long-run performance.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <TopNav logo={logo} />

      <main>
        <section className="px-8 pt-16 pb-12 md:pt-24 md:pb-16">
          <div className="max-w-5xl mx-auto">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-[0.2em] mb-4">
              Fall 2026 Experiential Learning Workshop
            </p>
            <h1 className="text-3xl md:text-5xl font-medium text-gray-900 leading-tight tracking-tight max-w-4xl">
              The Anti-Fragile Nexus: A Live Multiplayer Game of Systemic Friction and Global Resilience
            </h1>
            <p className="text-lg text-gray-600 leading-relaxed max-w-3xl mt-6">
              A hands-on, cloud-based war-room simulation where participants manage a global organization across the Triple Bottom Line and learn
              how local optimization can either build adaptive capacity or trigger systemic breakdown.
            </p>
            <p className="text-base text-gray-600 leading-relaxed max-w-3xl mt-4">
              This page intentionally shares instructional outcomes and program value without disclosing full game mechanics.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <a
                href="#program-view"
                className="inline-flex items-center justify-center bg-gray-900 text-white px-6 py-3 rounded text-sm font-medium hover:bg-gray-800 transition-colors"
              >
                Explore Workshop Structure
              </a>
              <a
                href="/contact"
                className="inline-flex items-center justify-center border border-gray-300 text-gray-700 px-6 py-3 rounded text-sm font-medium hover:border-gray-400 hover:text-gray-900 transition-colors"
              >
                Book a Private Demo Now
              </a>
            </div>
          </div>
        </section>

        <section className="px-8 pb-16">
          <div className="max-w-5xl mx-auto">
            <div className="bg-gray-50 border border-gray-200 rounded-2xl p-5 md:p-8">
              <img
                src="/images/experiential_learning_with_veydra.png"
                alt="Experiential learning with simulation"
                className="w-full h-auto rounded-xl border border-gray-200"
                loading="lazy"
              />
            </div>
          </div>
        </section>

        <section className="px-8 py-14 bg-gray-50 border-y border-gray-100">
          <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-white border border-gray-200 rounded-lg p-5">
              <p className="text-xs uppercase tracking-wider text-gray-500 mb-2">Format</p>
              <p className="text-sm text-gray-900 font-medium">Live multiplayer, cloud-based</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-5">
              <p className="text-xs uppercase tracking-wider text-gray-500 mb-2">Duration</p>
              <p className="text-sm text-gray-900 font-medium">1.5 hours</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-5">
              <p className="text-xs uppercase tracking-wider text-gray-500 mb-2">Audience Size</p>
              <p className="text-sm text-gray-900 font-medium">40 to 50 participants</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-5">
              <p className="text-xs uppercase tracking-wider text-gray-500 mb-2">Delivery</p>
              <p className="text-sm text-gray-900 font-medium">In-person, remote, or hybrid</p>
            </div>
          </div>
        </section>

        <section className="px-8 py-16" id="program-view">
          <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-7">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-[0.2em] mb-3">Program or Chair View</p>
              <h2 className="text-2xl md:text-3xl font-medium text-gray-900 tracking-tight mb-6">
                Why this workshop stands out in Fall 2026 programming
              </h2>
              <p className="text-gray-600 leading-relaxed mb-5">
                Traditional supply chain games often treat social and environmental effects as side notes. This workshop makes those forces first-class
                stocks and shows how they interact with financial pressure over time.
              </p>
              <p className="text-gray-600 leading-relaxed mb-8">
                Participants self-organize into cross-functional roles and confront systemic friction between local KPIs and whole-system resilience.
                The design helps instructors and administrators demonstrate not only what decisions were made, but why certain policies create delayed
                instability.
              </p>

              <div className="space-y-5">
                {instructorBenefits.map((item) => (
                  <div key={item.title} className="border border-gray-200 rounded-lg p-5">
                    <h3 className="text-base font-medium text-gray-900 mb-2">{item.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{item.body}</p>
                  </div>
                ))}
              </div>
            </div>

            <aside className="lg:col-span-5">
              <div className="bg-gray-900 text-white rounded-xl p-6">
                <p className="text-xs uppercase tracking-[0.2em] text-gray-300 mb-3">Learning Objectives</p>
                <ul className="space-y-3">
                  {outcomes.map((outcome) => (
                    <li key={outcome} className="text-sm leading-relaxed flex gap-2">
                      <span className="text-gray-300">+</span>
                      <span>{outcome}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-6 bg-white border border-gray-200 rounded-xl p-6">
                <p className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-3">Participant Takeaways</p>
                <ul className="space-y-3 text-sm text-gray-700 leading-relaxed">
                  <li>Confidence in discussing non-linear resilience with operational and policy audiences.</li>
                  <li>Practical intuition for lag effects, tipping points, and cross-functional tradeoffs.</li>
                  <li>A shared systems vocabulary teams can carry into strategy, operations, and governance discussions.</li>
                </ul>
              </div>
            </aside>
          </div>
        </section>

        <section className="px-8 py-16 bg-gray-50 border-y border-gray-100">
          <div className="max-w-5xl mx-auto">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-[0.2em] mb-3">Instructional Arc</p>
            <h2 className="text-2xl md:text-3xl font-medium text-gray-900 tracking-tight mb-10">
              Three phases designed for action, reflection, and transfer
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {phases.map((item) => (
                <div key={item.phase} className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-base font-medium text-gray-900 mb-3">{item.phase}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{item.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="px-8 py-16">
          <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            <div className="lg:col-span-8">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-[0.2em] mb-3">Participant Requirements and Logistics</p>
              <h2 className="text-2xl md:text-3xl font-medium text-gray-900 tracking-tight mb-6">
                Easy to run, rigorous in outcomes
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 text-sm text-gray-700">
                <div className="border border-gray-200 rounded-lg p-5">
                  <p className="font-medium text-gray-900 mb-2">Experience Level</p>
                  <p>Accessible to beginners, while still deep enough for advanced quantitative practitioners and seasoned systems thinkers.</p>
                </div>
                <div className="border border-gray-200 rounded-lg p-5">
                  <p className="font-medium text-gray-900 mb-2">What to Bring</p>
                  <p>Laptop or newer smartphone with a modern browser and reliable internet connection.</p>
                </div>
                <div className="border border-gray-200 rounded-lg p-5">
                  <p className="font-medium text-gray-900 mb-2">Preparation</p>
                  <p>No advance reading required. Familiarity with the bullwhip effect is helpful but optional.</p>
                </div>
                <div className="border border-gray-200 rounded-lg p-5">
                  <p className="font-medium text-gray-900 mb-2">Program Note</p>
                  <p>Structured to provide behavioral evidence aligned with the model in Quantifying Fragility as Systemic Debt.</p>
                </div>
              </div>
            </div>

            <div className="lg:col-span-4">
              <div className="bg-gray-900 rounded-xl p-6 text-white">
                <p className="text-xs uppercase tracking-[0.2em] text-gray-300 mb-2">Private Demo</p>
                <h3 className="text-xl font-medium mb-3">See if this fits your program goals</h3>
                <p className="text-sm text-gray-200 leading-relaxed mb-6">
                  Book a focused walkthrough for your faculty or administrative team to review fit, format, and deployment options for Fall 2026.
                </p>
                <a
                  href="/contact"
                  className="inline-flex items-center justify-center w-full bg-white text-gray-900 px-5 py-3 rounded text-sm font-medium hover:bg-gray-100 transition-colors"
                >
                  Book a Private Demo Now
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer logo={logo} />
    </div>
  );
}

export default AntiFragileNexusPage;